import { supabase } from '../lib/supabase-auth';
import { showSuccess, showInfo } from '../utils/toast';

export interface InvitationNotification {
  id: string;
  type: 'invitation_sent' | 'invitation_accepted' | 'invitation_expired' | 'invitation_cancelled' | 'invitation_resent';
  invitation_id: string;
  party_id: string;
  party_name: string;
  user_email: string;
  user_name: string;
  advisor_id: string;
  message: string;
  created_at: string;
  read: boolean;
}

class InvitationNotificationService {
  private subscriptions: Map<string, any> = new Map();

  // Subscribe to invitation changes for a specific party
  subscribeToPartyInvitations(partyId: string, onNotification: (notification: InvitationNotification) => void) {
    const channelName = `party-invitations-${partyId}`;

    // Unsubscribe from existing channel if any
    if (this.subscriptions.has(channelName)) {
      this.unsubscribe(channelName);
    }

    const subscription = supabase
      .channel(channelName)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'client_invitations',
        filter: `party_id=eq.${partyId}`
      }, async (payload: any) => {
        // Handle invitation changes
        if (payload.eventType === 'INSERT') {
          await this.handleInvitationSent(payload.new, onNotification);
        } else if (payload.eventType === 'UPDATE') {
          await this.handleInvitationUpdate(payload.old, payload.new, onNotification);
        }
      })
      .subscribe();

    this.subscriptions.set(channelName, subscription);
    return channelName;
  }

  // Subscribe to all invitations for advisors
  subscribeToAllInvitations(advisorId: string, onNotification: (notification: InvitationNotification) => void) {
    const channelName = `advisor-invitations-${advisorId}`;

    if (this.subscriptions.has(channelName)) {
      this.unsubscribe(channelName);
    }

    const subscription = supabase
      .channel(channelName)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'client_invitations',
        filter: `invited_by=eq.${advisorId}`
      }, async (payload: any) => {
        if (payload.eventType === 'INSERT') {
          await this.handleInvitationSent(payload.new, onNotification);
        } else if (payload.eventType === 'UPDATE') {
          await this.handleInvitationUpdate(payload.old, payload.new, onNotification);
        }
      })
      .subscribe();

    this.subscriptions.set(channelName, subscription);
    return channelName;
  }

  private async handleInvitationSent(invitation: any, onNotification: (notification: InvitationNotification) => void) {
    // Get party details
    const { data: party } = await supabase
      .from('parties')
      .select('name')
      .eq('id', invitation.party_id)
      .single();

    const notification: InvitationNotification = {
      id: crypto.randomUUID(),
      type: 'invitation_sent',
      invitation_id: invitation.id,
      party_id: invitation.party_id,
      party_name: party?.name || 'Unknown',
      user_email: invitation.email,
      user_name: invitation.full_name,
      advisor_id: invitation.invited_by,
      message: `Invitation sent to ${invitation.full_name} (${invitation.email})`,
      created_at: new Date().toISOString(),
      read: false
    };

    onNotification(notification);

    // Show toast notification
    showSuccess('Invitation sent successfully');
  }

  private async handleInvitationUpdate(oldInvitation: any, newInvitation: any, onNotification: (notification: InvitationNotification) => void) {
    // Get party details
    const { data: party } = await supabase
      .from('parties')
      .select('name')
      .eq('id', newInvitation.party_id)
      .single();

    let notification: InvitationNotification | null = null;

    // Check for status changes
    if (oldInvitation.status !== newInvitation.status) {
      if (newInvitation.status === 'accepted') {
        notification = {
          id: crypto.randomUUID(),
          type: 'invitation_accepted',
          invitation_id: newInvitation.id,
          party_id: newInvitation.party_id,
          party_name: party?.name || 'Unknown',
          user_email: newInvitation.email,
          user_name: newInvitation.full_name,
          advisor_id: newInvitation.invited_by,
          message: `${newInvitation.full_name} has accepted their invitation!`,
          created_at: new Date().toISOString(),
          read: false
        };

        showSuccess(`${newInvitation.full_name} has joined the portal!`);
      } else if (newInvitation.status === 'cancelled') {
        notification = {
          id: crypto.randomUUID(),
          type: 'invitation_cancelled',
          invitation_id: newInvitation.id,
          party_id: newInvitation.party_id,
          party_name: party?.name || 'Unknown',
          user_email: newInvitation.email,
          user_name: newInvitation.full_name,
          advisor_id: newInvitation.invited_by,
          message: `Invitation to ${newInvitation.full_name} was cancelled`,
          created_at: new Date().toISOString(),
          read: false
        };
      } else if (newInvitation.status === 'expired') {
        notification = {
          id: crypto.randomUUID(),
          type: 'invitation_expired',
          invitation_id: newInvitation.id,
          party_id: newInvitation.party_id,
          party_name: party?.name || 'Unknown',
          user_email: newInvitation.email,
          user_name: newInvitation.full_name,
          advisor_id: newInvitation.invited_by,
          message: `Invitation to ${newInvitation.full_name} has expired`,
          created_at: new Date().toISOString(),
          read: false
        };
      }
    }

    // Check for resent invitations
    if (oldInvitation.resent_count !== newInvitation.resent_count) {
      notification = {
        id: crypto.randomUUID(),
        type: 'invitation_resent',
        invitation_id: newInvitation.id,
        party_id: newInvitation.party_id,
        party_name: party?.name || 'Unknown',
        user_email: newInvitation.email,
        user_name: newInvitation.full_name,
        advisor_id: newInvitation.invited_by,
        message: `Invitation resent to ${newInvitation.full_name}`,
        created_at: new Date().toISOString(),
        read: false
      };

      showSuccess('Invitation resent successfully');
    }

    if (notification) {
      onNotification(notification);
    }
  }

  // Store notification in database
  async storeNotification(notification: InvitationNotification) {
    try {
      const { error } = await supabase
        .from('client_notifications')
        .insert({
          type: 'invitation_event',
          title: this.getNotificationTitle(notification.type),
          message: notification.message,
          metadata: {
            invitation_id: notification.invitation_id,
            party_id: notification.party_id,
            party_name: notification.party_name,
            user_email: notification.user_email,
            user_name: notification.user_name,
            event_type: notification.type
          },
          priority: notification.type === 'invitation_accepted' ? 'high' : 'medium',
          category: 'client_management',
          read: false,
          created_at: notification.created_at
        });

      if (error) {
        console.error('Error storing notification:', error);
      }
    } catch (err) {
      console.error('Failed to store notification:', err);
    }
  }

  private getNotificationTitle(type: string): string {
    switch (type) {
      case 'invitation_sent':
        return 'Invitation Sent';
      case 'invitation_accepted':
        return 'Invitation Accepted!';
      case 'invitation_expired':
        return 'Invitation Expired';
      case 'invitation_cancelled':
        return 'Invitation Cancelled';
      case 'invitation_resent':
        return 'Invitation Resent';
      default:
        return 'Invitation Update';
    }
  }

  unsubscribe(channelName: string) {
    const subscription = this.subscriptions.get(channelName);
    if (subscription) {
      subscription.unsubscribe();
      this.subscriptions.delete(channelName);
    }
  }

  unsubscribeAll() {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
    this.subscriptions.clear();
  }
}

export const invitationNotificationService = new InvitationNotificationService();